import React, { useState, useRef, useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { FormattedMessage, defineMessages } from 'react-intl'
import { ARM_MORTGAGE_TYPE, FIX_MORTGAGE_TYPE } from 'src/constants/loan'
import { useFormatMessage } from 'src/providers/intl'
import { selectTcaRequested, selectTcaError } from 'src/store/selectors/tca'
import { selectContactFirstName } from 'src/store/selectors/contact'
import { Button, Text, If, Unless, StatusIcon } from 'src/Components'
import HeightFade from 'src/Components/shared/Transition/HeightFade'
import BaseButton from 'src/Components/shared/Button/BaseButton'
import optInGifSource from './mortgage-coach-opt-in.gif'

import styles from './MortgageCoachPanel.module.scss'

const propTypes = {
  refiDetails: PropTypes.shape({
    years: PropTypes.number,
    type: PropTypes.string
  }).isRequired,
  handleConfirm: PropTypes.func.isRequired,
  handleClose: PropTypes.func
}

const MortgageCoachConfirm = ({ refiDetails, handleConfirm, handleClose }) => {
  const intlFormat = useFormatMessage()
  const fillRef = useRef(null)
  const disclaimerRef = useRef(null)

  const requested = useSelector(selectTcaRequested)
  const errored = useSelector(selectTcaError)
  const contactFirstName = useSelector(selectContactFirstName)

  const [fillHeight, setFillHeight] = useState('0')
  const [showFullDisclaimer, setShowFullDisclaimer] = useState(false)
  const toggleFullDisclaimer = () => {
    if (disclaimerRef.current) {
      setFillHeight(fillRef.current ? fillRef.current.clientHeight : '0')

      disclaimerRef.current.animate(!showFullDisclaimer ? 'fast' : 0)
    }
    setShowFullDisclaimer(!showFullDisclaimer)
  }

  const scrollToRef = useCallback(ref => {
    const interval = setInterval(
      () => ref.current && ref.current.scrollIntoView(false),
      25
    )
    const timeout = setTimeout(() => clearInterval(interval), 750)

    return () => {
      interval && clearInterval(interval)
      timeout && clearTimeout(timeout)
    }
  }, [])

  useEffect(() => {
    if (showFullDisclaimer) {
      scrollToRef(disclaimerRef)
    }
  }, [scrollToRef, showFullDisclaimer])

  const { type, years } = refiDetails || {}

  let refiType
  switch (type) {
    case FIX_MORTGAGE_TYPE:
      refiType = `${years}-year fixed`
      break
    case ARM_MORTGAGE_TYPE:
      refiType = `${years}/1 ARM`
      break
    default:
      refiType = `${years}-year ${type}`
      break
  }

  return (
    <>
      <div className={styles.modalInfo}>
        <div className={styles.imageWrapper}>
          <img
            className={styles.image}
            src={optInGifSource}
            alt={intlFormat(messages.gifAlt)}
          />
        </div>
        <Unless condition={errored}>
          <Text className={styles.tcaHeader} size='xl' align='center'>
            <FormattedMessage
              id='MortgageCoach.optInTCA.headline'
              defaultMessage='Compare costs and savings of your current loan versus a {refiType} refi'
              values={{ refiType }}
              data-qa='TCAHeadline'
            />
          </Text>
          <Text
            className={styles.tcaInfo}
            size='xs'
            color='quiet'
            align='center'
          >
            <FormattedMessage
              id='MortgageCoach.optInTCA.subhead'
              defaultMessage='{contactFirstName} uses Mortgage Coach — a tool to provide clients with Total Cost Analysis reports'
              values={{ contactFirstName }}
            />
          </Text>
        </Unless>
        <If condition={errored}>
          <div className={styles.error}>
            <StatusIcon failure />
            <Text size='l' weight='light' data-qa='TCAErrorMessage'>
              <FormattedMessage
                id='MortgageCoach.confirmTCA.error'
                defaultMessage='Hmm... something isn’t right. Don’t worry- we’ve notified our team! Please try again or contact {contactFirstName} for help.'
                values={{ contactFirstName }}
              />
            </Text>
            <Button
              className={classnames(styles.modalButton, styles.button)}
              onClick={handleClose}
              data-qa='requestTCA'
            >
              <FormattedMessage
                id='MortgageCoach.optInTCA.button.errored'
                defaultMessage='Return to refi'
              />
            </Button>
          </div>
        </If>
      </div>
      <div
        ref={fillRef}
        className={styles.fill}
        style={{ minHeight: fillHeight }}
      />
      <Unless condition={errored}>
        <Button
          className={classnames(styles.modalButton, styles.button)}
          primary
          loading={requested}
          onClick={handleConfirm}
          data-qa='requestTCA'
        >
          <FormattedMessage
            id='MortgageCoach.optInTCA.button'
            defaultMessage="Let's do it!"
          />
        </Button>
        <Text
          className={styles.disclaimer}
          size='tiny'
          color='quiet'
          align='center'
          data-qa='TCADisclaimer'
        >
          <FormattedMessage
            id='MortgageCoach.optInTCA.disclaimer'
            defaultMessage="By continuing, you're giving us permission to share {certainDataDisclaimerButton} with {mortgageCoachDisclaimerButton} so they can build these reports for you."
            values={{
              certainDataDisclaimerButton: DisclaimerButton({
                message: intlFormat(messages.certainData),
                onClick: toggleFullDisclaimer
              }),
              mortgageCoachDisclaimerButton: DisclaimerButton({
                message: intlFormat(messages.mortgageCoach),
                onClick: toggleFullDisclaimer
              })
            }}
          />
        </Text>
        <HeightFade
          ref={disclaimerRef}
          speed='fast'
          className={styles.heightfade}
        >
          <If condition={showFullDisclaimer}>
            <Text
              className={styles.disclaimer}
              size='tiny'
              color='quiet'
              align='center'
              data-qa='TCAFullDisclaimer'
            >
              <FormattedMessage
                id='MortgageCoach.optInTCA.fullDisclaimer'
                defaultMessage='Mortgage Coach is an independent, unaffiliated company providing a service. Upon acceptance, Homebot will send Mortgage Coach your first and last name, email address, property address, and the estimated value of your property if you or an agent has modified the automated value Homebot provided originally. We do not have permission to send the original estimated property values provided to use via our third-party supplier of those values. Homebot will also send your loan details, which includes loan amount, loan origination date, loan term, product and loan interest rate.'
              />
            </Text>
            <Button
              minimal
              className={classnames(
                styles.modalButton,
                styles.button,
                styles.closeDisclaimerButton
              )}
              onClick={toggleFullDisclaimer}
              data-qa='closeDisclaimerButton'
            >
              <FormattedMessage
                id='MortgageCoach.optInTCA.closeDisclaimerButton'
                defaultMessage='Show less'
              />
            </Button>
          </If>
        </HeightFade>
      </Unless>
    </>
  )
}

const DisclaimerButton = ({ message, onClick }) => (
  <BaseButton className={styles.disclaimerButton} onClick={onClick}>
    {message}
  </BaseButton>
)

DisclaimerButton.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.string,
    defaultMessage: PropTypes.string
  }),
  onClick: PropTypes.func
}
MortgageCoachConfirm.propTypes = propTypes

export default MortgageCoachConfirm

const messages = defineMessages({
  gifAlt: {
    id: 'MortgageCoach.confirmTCA.describeAlt',
    defaultMessage: 'Total Cost Analysis from Mortgage Coach'
  },
  certainData: {
    id: 'MortgageCoach.optInTCA.certainDataDisclaimerButton',
    defaultMessage: 'certain data'
  },
  mortgageCoach: {
    id: 'MortgageCoach.optInTCA.mortgageCoachDisclaimerButton',
    defaultMessage: 'Mortgage Coach'
  }
})
